import { useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCosmetics } from '../contexts/CosmeticsContext';
import { usePortfolio, useInstruments } from '../hooks/useApi';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import { FinancialTooltip } from '../components/FinancialTooltip';
import { 
  Search, 
  Plus, 
  Minus, 
  Zap, 
  TrendingUp, 
  TrendingDown,
  AlertCircle,
  Check,
  X,
  DollarSign,
  BarChart3,
  Shuffle,
  Target,
  Loader2,
  Copy,
  Sparkles,
  RotateCcw,
  Info,
  ChevronLeft,
  ChevronRight,
  Globe,
  Building2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PortfolioPage = () => {
  const { user, token } = useAuth();
  const { themeImage, themeColors, cardStyleDetail, badgeDetail } = useCosmetics();
  const { portfolio, summary, validation, addHolding, removeHolding, setPowerPlay, refresh, loading } = usePortfolio();
  const { instruments, pricingStatus, searchInstruments } = useInstruments();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedType, setSelectedType] = useState('all');
  const [buyModalOpen, setBuyModalOpen] = useState(false);
  const [sellModalOpen, setSellModalOpen] = useState(false);
  const [autoPickModalOpen, setAutoPickModalOpen] = useState(false);
  const [selectedInstrument, setSelectedInstrument] = useState(null);
  const [selectedHolding, setSelectedHolding] = useState(null);
  const [buyAmount, setBuyAmount] = useState(5000);
  const [sellAmount, setSellAmount] = useState(0);
  const [sellAll, setSellAll] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [autoPickStrategy, setAutoPickStrategy] = useState('balanced');
  const [autoPickLoading, setAutoPickLoading] = useState(false);
  const [visibleCount, setVisibleCount] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 50;

  // Asset detail modal
  const [assetDetailOpen, setAssetDetailOpen] = useState(false);
  const [assetDetail, setAssetDetail] = useState(null);
  const [assetDetailLoading, setAssetDetailLoading] = useState(false);
  const [fundamentals, setFundamentals] = useState(null);
  const [fundamentalsLoading, setFundamentalsLoading] = useState(false);

  const MIN_AMOUNT = 100; // V$100 minimum

  const holdingSymbols = useMemo(() => 
    portfolio?.holdings?.map(h => h.symbol) || [],
    [portfolio?.holdings]
  );

  // Lookup map: symbol -> instrument name
  const instrumentNames = useMemo(() => {
    const map = {};
    instruments.forEach(i => { map[i.symbol] = i.name; });
    return map;
  }, [instruments]);

  const filteredInstruments = useMemo(() => {
    let filtered = instruments;
    if (selectedType !== 'all') {
      filtered = instruments.filter(i => i.type === selectedType);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(i => 
        i.symbol.toLowerCase().includes(q) || 
        i.name.toLowerCase().includes(q)
      );
    }
    return filtered;
  }, [instruments, selectedType, searchQuery]);

  const handleSearch = async (query) => {
    setSearchQuery(query);
    setCurrentPage(1);
    if (query.length >= 2) {
      try {
        const results = await searchInstruments(query);
        setSearchResults(results);
      } catch (err) {
        console.error('Search failed:', err);
      }
    } else {
      setSearchResults([]);
    }
  };

  const openBuyModal = (instrument) => {
    setSelectedInstrument(instrument);
    const maxAmount = Math.min(
      summary?.cash_remaining || 100000,
      (summary?.total_value_vd || 100000) * 0.15 // 15% max per instrument
    );
    setBuyAmount(Math.min(5000, Math.max(MIN_AMOUNT, maxAmount)));
    setBuyModalOpen(true);
  };

  const openSellModal = (holding) => {
    setSelectedHolding(holding);
    const holdingValue = holding.units * holding.current_price_vd;
    setSellAmount(Math.min(holdingValue / 2, holdingValue - MIN_AMOUNT)); // Default to 50% or leave minimum
    setSellAll(false);
    setSellModalOpen(true);
  };

  const handleResetPortfolio = async () => {
    if (!window.confirm('Are you sure you want to reset your portfolio? This will sell all holdings and restore V$100,000.')) return;
    setIsProcessing(true);
    try {
      await fetch(`${API_URL}/api/portfolio/reset`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      toast.success('Portfolio reset to V$100,000');
      window.location.reload();
    } catch (err) {
      toast.error('Failed to reset portfolio');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBuy = async () => {
    if (!selectedInstrument || buyAmount < MIN_AMOUNT) {
      toast.error(`Minimum purchase is V$${MIN_AMOUNT}`);
      return;
    }
    setIsProcessing(true);
    try {
      await addHolding(selectedInstrument.symbol, buyAmount);
      toast.success(`Added ${selectedInstrument.symbol} to portfolio`);
      setBuyModalOpen(false);
      setSelectedInstrument(null);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSell = async () => {
    if (!selectedHolding) return;
    setIsProcessing(true);
    try {
      if (sellAll) {
        await removeHolding(selectedHolding.symbol);
        toast.success(`Sold all ${selectedHolding.symbol}`);
      } else {
        await removeHolding(selectedHolding.symbol, sellAmount);
        toast.success(`Sold V$${sellAmount.toLocaleString()} of ${selectedHolding.symbol}`);
      }
      setSellModalOpen(false);
      setSelectedHolding(null);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSetPowerPlay = async (symbol) => {
    setIsProcessing(true);
    try {
      await setPowerPlay(symbol);
      toast.success(`${symbol} is now your Power Play (3x points)`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAutoPick = async () => {
    setAutoPickLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/portfolio/auto-pick?strategy=${autoPickStrategy}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to auto-pick portfolio');
      }
      
      const result = await response.json();
      toast.success(result.message);
      setAutoPickModalOpen(false);
      refresh(); // Refresh portfolio data
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAutoPickLoading(false);
    }
  };

  const openAssetDetail = async (symbol, forceRefresh = false) => {
    if (!forceRefresh) {
      setAssetDetailOpen(true);
      setAssetDetailLoading(true);
      setAssetDetail(null);
      setFundamentals(null);
    }
    try {
      const res = await fetch(`${API_URL}/api/instruments/${symbol}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setAssetDetail(await res.json());
        setFundamentalsLoading(true);
        fetch(`${API_URL}/api/instruments/${symbol}/fundamentals${forceRefresh ? '?force=true' : ''}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(r => r.ok ? r.json() : null)
          .then(data => {
            if (data && !data.error) setFundamentals(data);
            if (forceRefresh) toast.success('Financial data refreshed!');
          })
          .catch(() => {})
          .finally(() => setFundamentalsLoading(false));
      } else {
        toast.error('Failed to load asset details');
        if (!forceRefresh) setAssetDetailOpen(false);
      }
    } catch {
      toast.error('Failed to load asset details');
      if (!forceRefresh) setAssetDetailOpen(false);
    } finally {
      setAssetDetailLoading(false);
    }
  };

  // Pagination for instrument browser
  const totalPages = Math.ceil(filteredInstruments.length / ITEMS_PER_PAGE);
  const paginatedInstruments = filteredInstruments.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleCopyForAI = () => {
    if (!portfolio?.holdings || portfolio.holdings.length === 0) {
      toast.error('No holdings to copy');
      return;
    }

    // Format portfolio data for AI analysis
    const holdings = portfolio.holdings;
    const totalValue = holdings.reduce((sum, h) => sum + (h.value_vd || h.quantity * h.current_price || 0), 0);
    
    let text = `=== TRADER TITANS PORTFOLIO DATA ===
Game: Fantasy Trading League
Season: 2026-Q1
Export Date: ${new Date().toISOString().split('T')[0]}

📊 PORTFOLIO SUMMARY
- Total Holdings: ${holdings.length}/10 instruments
- Portfolio Value: V$${totalValue.toLocaleString(undefined, {maximumFractionDigits: 0})}
- Cash Remaining: V$${(summary?.cash_remaining || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
- Power Play (3x): ${portfolio.power_play_symbol || 'Not set'}

📈 HOLDINGS BREAKDOWN
`;

    // Group by type
    const byType = {};
    holdings.forEach(h => {
      const type = h.type || 'Unknown';
      if (!byType[type]) byType[type] = [];
      byType[type].push(h);
    });

    Object.entries(byType).forEach(([type, items]) => {
      text += `\n${type.toUpperCase()}S:\n`;
      items.forEach(h => {
        const value = h.value_vd || (h.quantity * h.current_price) || 0;
        const pnl = h.current_price && (h.buy_price_vd || h.buy_price)
          ? ((h.current_price - (h.buy_price_vd || h.buy_price)) / (h.buy_price_vd || h.buy_price) * 100).toFixed(2)
          : '0.00';
        const pnlSign = parseFloat(pnl) >= 0 ? '+' : '';
        text += `  • ${h.symbol}: V$${value.toLocaleString(undefined, {maximumFractionDigits: 0})} (${pnlSign}${pnl}%)${portfolio.power_play_symbol === h.symbol ? ' ⚡3x' : ''}\n`;
      });
    });

    text += `
🎯 GAME CONTEXT
- Points are awarded for: price gains, stability, dividend events, recovery from dips
- Power Play earns 3x points on the selected instrument
- Goal: Maximize points, not just returns

💡 SUGGESTED PROMPTS FOR AI:
1. "Analyze my portfolio diversification and suggest improvements"
2. "Which holding should I set as my Power Play for maximum points?"
3. "What are the risks in my current allocation?"
4. "Suggest trades to optimize for the points-based scoring system"
`;

    navigator.clipboard.writeText(text).then(() => {
      toast.success('Portfolio copied! Paste into ChatGPT or Claude for AI analysis', {
        duration: 4000
      });
    }).catch(() => {
      toast.error('Failed to copy to clipboard');
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-pulse text-emerald-400">Loading portfolio...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20 w-full relative" style={{ background: themeColors?.background || '#020617' }} data-testid="portfolio-page">
      {/* Theme artwork background */}
      {themeImage && (
        <div
          className="fixed inset-0 pointer-events-none z-0"
          style={{
            backgroundImage: `url(${themeImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.07,
          }}
        />
      )}
      <div className="relative z-10">
      {/* Header */}
      <div className="border-b" style={{ borderColor: themeColors?.primary ? `${themeColors.primary}30` : '#1e293b', background: themeColors?.background ? `linear-gradient(to bottom, ${themeColors.background}, ${themeColors.background}ee)` : undefined }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-white">Portfolio Builder</h1>
                {pricingStatus?.is_pro ? (
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30" data-testid="live-badge">
                    <span className="inline-block w-2 h-2 bg-emerald-400 rounded-full mr-1.5 animate-pulse"></span>
                    Live (15min)
                  </Badge>
                ) : (
                  <Badge className="bg-slate-500/20 text-slate-400 border-slate-500/30" data-testid="delayed-badge">
                    <span className="inline-block w-2 h-2 bg-slate-400 rounded-full mr-1.5"></span>
                    60min Delayed
                  </Badge>
                )}
              </div>
              <p className="text-slate-400 mt-1">
                {portfolio?.holdings?.length || 0}/10 instruments selected
              </p>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              {/* Copy for AI Button - Show when has holdings */}
              {portfolio?.holdings && portfolio.holdings.length > 0 && (
                <Button 
                  onClick={handleCopyForAI}
                  variant="outline"
                  size="sm"
                  className="border-slate-600 hover:bg-slate-800"
                  data-testid="copy-for-ai-btn"
                >
                  <Sparkles className="h-4 w-4 mr-1 text-purple-400" />
                  AI
                </Button>
              )}
              {/* Auto Pick Button - Show when no holdings */}
              {(!portfolio?.holdings || portfolio.holdings.length === 0) && (
                <Button 
                  onClick={() => setAutoPickModalOpen(true)}
                  className="bg-purple-600 hover:bg-purple-500"
                  data-testid="auto-pick-btn"
                >
                  <Shuffle className="h-4 w-4 mr-2" />
                  Auto Pick
                </Button>
              )}
              <div className="text-right">
                <FinancialTooltip termKey="cash_balance">
                  <p className="text-slate-400 text-sm underline decoration-dotted decoration-slate-600 cursor-help">Cash Available</p>
                </FinancialTooltip>
                <p className="text-xl font-bold text-emerald-400" data-testid="cash-available">
                  V${summary?.cash_remaining?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '100,000'}
                </p>
              </div>
            </div>
          </div>

          {/* Validation Status */}
          {validation && (
            <div className={`mt-4 p-3 rounded-lg flex items-center gap-2 ${
              validation.valid 
                ? 'bg-emerald-500/10 border border-emerald-500/30' 
                : 'bg-amber-500/10 border border-amber-500/30'
            }`}>
              {validation.valid ? (
                <>
                  <Check className="h-5 w-5 text-emerald-500" />
                  <span className="text-emerald-400">Portfolio complete! Ready to score points.</span>
                </>
              ) : (
                <>
                  <AlertCircle className="h-5 w-5 text-amber-500" />
                  <span className="text-amber-400">{validation.issues?.[0]}</span>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-8">
          {/* Current Holdings */}
          <div className="lg:col-span-2">
            <Card className="bg-slate-900 border-slate-800 sticky top-24">
              <CardHeader className="px-3 sm:px-6 py-3 sm:py-4">
                <CardTitle className="text-white flex items-center justify-between text-base">
                  <FinancialTooltip termKey="holdings">
                    <span>Your Holdings</span>
                  </FinancialTooltip>
                  <Badge variant="outline" className="border-emerald-500/50 text-emerald-400">
                    {portfolio?.holdings?.length || 0}/10
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
                {portfolio?.holdings?.length > 0 ? (
                  <TooltipProvider delayDuration={200}>
                  <div className="space-y-2 max-h-[500px] overflow-y-auto">
                    {portfolio.holdings.map((holding) => {
                      const csImg = cardStyleDetail?.preview_data?.image_url;
                      const csBorder = cardStyleDetail?.preview_data?.border;
                      const csShadow = cardStyleDetail?.preview_data?.box_shadow;
                      return (
                      <div 
                        key={holding.symbol}
                        className={`p-2 sm:p-3 rounded-lg border cursor-pointer transition-colors hover:brightness-110 ${
                          holding.is_power_play ? 'border-amber-500/50' : !csBorder ? 'border-transparent hover:border-slate-600 bg-slate-800/50' : ''
                        }`}
                        style={csImg ? {
                          backgroundImage: `linear-gradient(rgba(15,23,42,0.82), rgba(15,23,42,0.82)), url(${csImg})`,
                          backgroundSize: 'cover', backgroundPosition: 'center',
                          border: !holding.is_power_play ? csBorder : undefined,
                          boxShadow: csShadow,
                        } : {}}
                        onClick={() => openAssetDetail(holding.symbol)}
                        data-testid={`holding-card-${holding.symbol}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 min-w-0">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="font-semibold text-white text-sm cursor-help">{holding.symbol}</span>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="bg-slate-800 border-slate-600 text-white">
                                <p className="text-xs">{holding.symbol} — {instrumentNames[holding.symbol] || 'Loading...'}</p>
                              </TooltipContent>
                            </Tooltip>
                            {holding.is_power_play && (
                              <Badge className="bg-amber-500/20 text-amber-400 text-[10px] px-1 py-0">
                                <Zap className="h-2.5 w-2.5 mr-0.5" />3x
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-0.5">
                            <span className="text-white text-sm font-medium">
                              V${(holding.units * holding.current_price_vd).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            </span>
                            <span className={`text-xs ml-1 ${
                              holding.current_price_vd >= holding.buy_price_vd ? 'text-emerald-400' : 'text-red-400'
                            }`}>
                              {((holding.current_price_vd - holding.buy_price_vd) / holding.buy_price_vd * 100).toFixed(1)}%
                            </span>
                            {!holding.is_power_play && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => { e.stopPropagation(); handleSetPowerPlay(holding.symbol); }}
                                className="h-6 w-6 p-0 text-amber-400 hover:bg-amber-500/10 ml-1"
                                disabled={isProcessing}
                                data-testid={`power-play-btn-${holding.symbol}`}
                              >
                                <Zap className="h-3 w-3" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); openSellModal(holding); }}
                              className="h-6 w-6 p-0 text-red-400 hover:bg-red-500/10"
                              disabled={isProcessing}
                              data-testid={`sell-btn-${holding.symbol}`}
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                  </TooltipProvider>
                ) : (
                  <div className="text-center py-8 text-slate-400">
                    <p>No holdings yet</p>
                    <p className="text-sm mt-1">Browse instruments to add to your portfolio</p>
                  </div>
                )}

                {/* Composition Summary */}
                {portfolio?.holdings?.length > 0 && validation?.by_type && (
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <FinancialTooltip termKey="diversification">
                      <p className="text-slate-400 text-sm mb-2">Composition</p>
                    </FinancialTooltip>
                    <div className="flex flex-wrap gap-2">
                      <FinancialTooltip termKey="stock" showIcon={false}>
                        <Badge variant="outline" className="border-slate-600">
                          Stocks: {validation.by_type.stock || 0}/6
                        </Badge>
                      </FinancialTooltip>
                      <FinancialTooltip termKey="crypto" showIcon={false}>
                        <Badge variant="outline" className="border-slate-600">
                          Crypto: {validation.by_type.crypto || 0}/3
                        </Badge>
                      </FinancialTooltip>
                      <FinancialTooltip termKey="commodity" showIcon={false}>
                        <Badge variant="outline" className="border-slate-600">
                          Commodities: {validation.by_type.commodity || 0}/1
                        </Badge>
                      </FinancialTooltip>
                      <FinancialTooltip termKey="etf" showIcon={false}>
                        <Badge variant="outline" className="border-slate-600">
                          ETFs: {validation.by_type.etf || 0}/2
                        </Badge>
                      </FinancialTooltip>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Instrument Browser */}
          <div className="lg:col-span-3">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Browse Instruments</CardTitle>
                <div className="mt-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
                    <Input
                      placeholder="Search by symbol or name..."
                      className="pl-10 bg-slate-800 border-slate-700 text-white"
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                      data-testid="instrument-search"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Tabs value={selectedType} onValueChange={(val) => { setSelectedType(val); setCurrentPage(1); }}>
                  <TabsList className="bg-slate-800 mb-4 flex flex-wrap h-auto gap-1">
                    <TabsTrigger value="all" className="text-xs">All</TabsTrigger>
                    <TabsTrigger value="stock" className="text-xs">Stocks</TabsTrigger>
                    <TabsTrigger value="crypto" className="text-xs">Crypto</TabsTrigger>
                    <TabsTrigger value="commodity" className="text-xs">Comm.</TabsTrigger>
                    <TabsTrigger value="etf" className="text-xs">ETFs</TabsTrigger>
                  </TabsList>

                  <p className="text-xs text-slate-500 mb-2" data-testid="instrument-count">
                    Showing {Math.min(paginatedInstruments.length, ITEMS_PER_PAGE)} of {filteredInstruments.length} instruments {selectedType !== 'all' ? `(${selectedType})` : ''} — Page {currentPage}/{totalPages || 1}
                  </p>
                  <div className="space-y-2">
                    {paginatedInstruments.map((instrument) => {
                      const isOwned = holdingSymbols.includes(instrument.symbol);
                      return (
                        <div
                          key={instrument.symbol}
                          className={`p-2 sm:p-4 rounded-lg border transition-colors cursor-pointer ${
                            isOwned 
                              ? 'bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/15' 
                              : 'bg-slate-800/50 border-slate-700 hover:border-slate-600 hover:bg-slate-800'
                          }`}
                          onClick={() => openAssetDetail(instrument.symbol)}
                          data-testid={`instrument-${instrument.symbol}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-slate-700 flex items-center justify-center shrink-0">
                                <span className="text-xs sm:text-sm font-bold text-white">
                                  {instrument.symbol.slice(0, 2)}
                                </span>
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="font-semibold text-white text-sm">{instrument.symbol}</span>
                                  <Badge variant="outline" className="text-[10px] px-1 py-0 border-slate-600 text-slate-400">
                                    {instrument.type}
                                  </Badge>
                                  {isOwned && (
                                    <Badge className="bg-emerald-500/20 text-emerald-400 text-[10px] px-1 py-0">
                                      Owned
                                    </Badge>
                                  )}
                                </div>
                                <span className="text-slate-400 text-xs truncate block">{instrument.name}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <div className="text-right">
                                <p className="text-white font-medium text-sm">
                                  V${instrument.current_price_vd?.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                </p>
                                <div className="flex items-center gap-1 justify-end text-[10px]">
                                  <span className={`${
                                    (instrument.change_pct || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'
                                  }`}>
                                    {(instrument.change_pct || 0) >= 0 ? '+' : ''}{(instrument.change_pct || 0).toFixed(1)}%
                                  </span>
                                  {instrument.week_open && instrument.current_price_vd && (
                                    <span className={`hidden sm:inline ${
                                      ((instrument.current_price_vd - instrument.week_open) / instrument.week_open * 100) >= 0 
                                        ? 'text-emerald-400/60' : 'text-red-400/60'
                                    }`}>
                                      wk:{((instrument.current_price_vd - instrument.week_open) / instrument.week_open * 100).toFixed(1)}%
                                    </span>
                                  )}
                                </div>
                              </div>
                              {!isOwned && portfolio?.holdings?.length < 10 && (
                                <Button
                                  size="sm"
                                  onClick={(e) => { e.stopPropagation(); openBuyModal(instrument); }}
                                  className="bg-emerald-600 hover:bg-emerald-700 h-7 w-7 p-0 sm:h-8 sm:w-8"
                                  disabled={isProcessing}
                                  data-testid={`buy-btn-${instrument.symbol}`}
                                >
                                  <Plus className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-700">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          className="border-slate-600 text-slate-300"
                          data-testid="instruments-prev-page"
                        >
                          <ChevronLeft className="h-4 w-4 mr-1" /> Prev
                        </Button>
                        <div className="flex items-center gap-1">
                          {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }
                            return (
                              <Button
                                key={pageNum}
                                variant={currentPage === pageNum ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setCurrentPage(pageNum)}
                                className={`h-8 w-8 p-0 ${currentPage === pageNum ? 'bg-emerald-600' : 'border-slate-600 text-slate-300'}`}
                              >
                                {pageNum}
                              </Button>
                            );
                          })}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                          className="border-slate-600 text-slate-300"
                          data-testid="instruments-next-page"
                        >
                          Next <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    )}
                  </div>
                </Tabs>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Buy Modal */}
      <Dialog open={buyModalOpen} onOpenChange={setBuyModalOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Add {selectedInstrument?.symbol} to Portfolio</DialogTitle>
          </DialogHeader>
          {selectedInstrument && (
            <div className="space-y-6 py-4">
              <div className="flex items-center justify-between p-4 bg-slate-800 rounded-lg">
                <div>
                  <p className="font-semibold">{selectedInstrument.name}</p>
                  <p className="text-slate-400 text-sm">{selectedInstrument.symbol}</p>
                </div>
                <div className="text-right">
                  <p className="text-emerald-400 font-semibold">
                    V${selectedInstrument.current_price_vd?.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </p>
                  <p className="text-slate-400 text-sm">per unit</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-slate-300">Investment Amount (V$)</Label>
                  <Input
                    type="number"
                    value={buyAmount}
                    onChange={(e) => setBuyAmount(Number(e.target.value))}
                    min={MIN_AMOUNT}
                    max={summary?.cash_remaining || 100000}
                    className="mt-2 bg-slate-800 border-slate-600"
                    data-testid="buy-amount-input"
                  />
                </div>
                <Slider
                  value={[buyAmount]}
                  onValueChange={([val]) => setBuyAmount(val)}
                  min={MIN_AMOUNT}
                  max={Math.min(summary?.cash_remaining || 100000, (summary?.total_value_vd || 100000) * 0.15)}
                  step={100}
                  className="mt-2"
                />
                <div className="flex justify-between text-sm text-slate-400">
                  <span>V${MIN_AMOUNT}</span>
                  <span>Max: V${Math.min(summary?.cash_remaining || 100000, (summary?.total_value_vd || 100000) * 0.15).toLocaleString()}</span>
                </div>
              </div>

              <div className="p-4 bg-slate-800/50 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Units to receive</span>
                  <span className="text-white">
                    {(buyAmount / selectedInstrument.current_price_vd).toFixed(4)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Cash after purchase</span>
                  <span className="text-white">
                    V${((summary?.cash_remaining || 100000) - buyAmount).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setBuyModalOpen(false)} className="border-slate-600">
              Cancel
            </Button>
            <Button 
              onClick={handleBuy} 
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={isProcessing || buyAmount < MIN_AMOUNT}
              data-testid="confirm-buy-btn"
            >
              {isProcessing ? 'Adding...' : 'Add to Portfolio'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sell Modal */}
      <Dialog open={sellModalOpen} onOpenChange={setSellModalOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Sell {selectedHolding?.symbol}</DialogTitle>
          </DialogHeader>
          {selectedHolding && (
            <div className="space-y-6 py-4">
              <div className="flex items-center justify-between p-4 bg-slate-800 rounded-lg">
                <div>
                  <p className="font-semibold">{selectedHolding.symbol}</p>
                  <p className="text-slate-400 text-sm">{selectedHolding.units.toFixed(4)} units</p>
                </div>
                <div className="text-right">
                  <p className="text-white font-semibold">
                    V${(selectedHolding.units * selectedHolding.current_price_vd).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </p>
                  <p className={`text-sm ${
                    selectedHolding.current_price_vd >= selectedHolding.buy_price_vd ? 'text-emerald-400' : 'text-red-400'
                  }`}>
                    {((selectedHolding.current_price_vd - selectedHolding.buy_price_vd) / selectedHolding.buy_price_vd * 100).toFixed(2)}%
                  </p>
                </div>
              </div>

              {/* Sell All Toggle */}
              <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                <div>
                  <p className="text-white font-medium">Sell Entire Position</p>
                  <p className="text-slate-400 text-sm">Remove this instrument from portfolio</p>
                </div>
                <Button
                  variant={sellAll ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSellAll(!sellAll)}
                  className={sellAll ? "bg-red-600 hover:bg-red-700" : "border-slate-600"}
                  data-testid="sell-all-toggle"
                >
                  {sellAll ? 'Yes, Sell All' : 'Partial Sell'}
                </Button>
              </div>

              {/* Partial Sell Amount */}
              {!sellAll && (
                <div className="space-y-4">
                  <div>
                    <Label className="text-slate-300">Amount to Sell (V$)</Label>
                    <Input
                      type="number"
                      value={sellAmount}
                      onChange={(e) => setSellAmount(Number(e.target.value))}
                      min={MIN_AMOUNT}
                      max={(selectedHolding.units * selectedHolding.current_price_vd) - MIN_AMOUNT}
                      className="mt-2 bg-slate-800 border-slate-600"
                      data-testid="sell-amount-input"
                    />
                  </div>
                  <Slider
                    value={[sellAmount]}
                    onValueChange={([val]) => setSellAmount(val)}
                    min={MIN_AMOUNT}
                    max={Math.max(MIN_AMOUNT, (selectedHolding.units * selectedHolding.current_price_vd) - MIN_AMOUNT)}
                    step={100}
                    className="mt-2"
                  />
                  <div className="flex justify-between text-sm text-slate-400">
                    <span>V${MIN_AMOUNT}</span>
                    <span>Max: V${Math.max(0, (selectedHolding.units * selectedHolding.current_price_vd) - MIN_AMOUNT).toLocaleString()}</span>
                  </div>
                </div>
              )}

              <div className="p-4 bg-slate-800/50 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">You will receive</span>
                  <span className="text-emerald-400 font-semibold">
                    V${(sellAll ? selectedHolding.units * selectedHolding.current_price_vd : sellAmount).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                </div>
                {!sellAll && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Remaining position</span>
                    <span className="text-white">
                      V${((selectedHolding.units * selectedHolding.current_price_vd) - sellAmount).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                  </div>
                )}
              </div>

              {sellAll && portfolio?.holdings?.length === 10 && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <p className="text-amber-400 text-sm">
                    <AlertCircle className="inline h-4 w-4 mr-1" />
                    Selling will reduce your portfolio below 10. You'll need to add a new instrument.
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSellModalOpen(false)} className="border-slate-600">
              Cancel
            </Button>
            <Button 
              onClick={handleSell} 
              className="bg-red-600 hover:bg-red-700"
              disabled={isProcessing || (!sellAll && sellAmount < MIN_AMOUNT)}
              data-testid="confirm-sell-btn"
            >
              {isProcessing ? 'Selling...' : (sellAll ? 'Sell All' : `Sell V$${sellAmount.toLocaleString()}`)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auto Pick Modal */}
      <Dialog open={autoPickModalOpen} onOpenChange={setAutoPickModalOpen}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Shuffle className="h-5 w-5 text-purple-400" />
              Auto Pick Portfolio
            </DialogTitle>
            <DialogDescription>
              Let us build a diversified portfolio for you. Choose your strategy:
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Strategy Selection */}
            <div className="grid gap-3">
              <StrategyOption 
                id="balanced"
                title="Balanced"
                description="5 stocks, 2 crypto, 1 commodity, 2 ETFs - follows game rules"
                icon={<Target className="h-5 w-5" />}
                selected={autoPickStrategy === 'balanced'}
                onSelect={() => setAutoPickStrategy('balanced')}
              />
              <StrategyOption 
                id="random"
                title="Random"
                description="Randomly pick 10 instruments for maximum variety"
                icon={<Shuffle className="h-5 w-5" />}
                selected={autoPickStrategy === 'random'}
                onSelect={() => setAutoPickStrategy('random')}
              />
              <StrategyOption 
                id="top_performers"
                title="Top Performers"
                description="Pick high-value instruments across asset types"
                icon={<TrendingUp className="h-5 w-5" />}
                selected={autoPickStrategy === 'top_performers'}
                onSelect={() => setAutoPickStrategy('top_performers')}
              />
            </div>

            <div className="bg-slate-800/50 rounded-lg p-3 text-sm">
              <p className="text-slate-400">
                <strong className="text-slate-300">Note:</strong> Allocation is weighted by asset type 
                (40% stocks, 30% crypto, 15% commodities, 15% ETFs). You can adjust positions after.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAutoPickModalOpen(false)} className="border-slate-600">
              Cancel
            </Button>
            <Button 
              onClick={handleAutoPick} 
              className="bg-purple-600 hover:bg-purple-500"
              disabled={autoPickLoading}
              data-testid="confirm-auto-pick-btn"
            >
              {autoPickLoading ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Building...</>
              ) : (
                <><Shuffle className="h-4 w-4 mr-2" /> Build Portfolio</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Asset Detail Modal */}
      <Dialog open={assetDetailOpen} onOpenChange={setAssetDetailOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <Info className="h-5 w-5 text-emerald-400" />
                {assetDetail ? `${assetDetail.symbol} — ${assetDetail.name}` : 'Loading...'}
              </DialogTitle>
              {assetDetail && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openAssetDetail(assetDetail.symbol, true)}
                  disabled={fundamentalsLoading}
                  className="text-slate-400 hover:text-white h-8"
                  data-testid="refresh-fundamentals-btn"
                >
                  <RotateCcw className={`h-3.5 w-3.5 mr-1 ${fundamentalsLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              )}
            </div>
            <DialogDescription>
              {assetDetail ? `${assetDetail.type?.charAt(0).toUpperCase() + assetDetail.type?.slice(1)} · ${assetDetail.exchange || 'N/A'} · ${assetDetail.currency || 'USD'}` : ''}
            </DialogDescription>
          </DialogHeader>
          {assetDetailLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
            </div>
          ) : assetDetail ? (
            <div className="space-y-4">
              {/* Price Info */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                  <p className="text-slate-400 text-xs">Current Price</p>
                  <p className="text-xl font-bold text-white">V${assetDetail.current_price_vd?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                  <p className="text-slate-400 text-xs">Change</p>
                  <p className={`text-xl font-bold ${(assetDetail.change_pct || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {(assetDetail.change_pct || 0) >= 0 ? '+' : ''}{(assetDetail.change_pct || 0).toFixed(2)}%
                  </p>
                </div>
              </div>

              {/* Weekly Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-lg bg-slate-800/30 text-center">
                  <p className="text-slate-500 text-[10px] uppercase">Week Open</p>
                  <p className="text-white font-medium text-sm">V${assetDetail.week_open?.toLocaleString(undefined, { maximumFractionDigits: 2 }) || '—'}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/30 text-center">
                  <p className="text-slate-500 text-[10px] uppercase">Week High</p>
                  <p className="text-emerald-400 font-medium text-sm">V${assetDetail.week_high?.toLocaleString(undefined, { maximumFractionDigits: 2 }) || '—'}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/30 text-center">
                  <p className="text-slate-500 text-[10px] uppercase">Week Low</p>
                  <p className="text-red-400 font-medium text-sm">V${assetDetail.week_low?.toLocaleString(undefined, { maximumFractionDigits: 2 }) || '—'}</p>
                </div>
              </div>

              {/* Meta Info */}
              <div className="space-y-2 text-sm">
                {assetDetail.sector && (
                  <div className="flex items-center justify-between py-1.5 border-b border-slate-800">
                    <span className="text-slate-400 flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> Sector</span>
                    <span className="text-white">{assetDetail.sector}</span>
                  </div>
                )}
                <div className="flex items-center justify-between py-1.5 border-b border-slate-800">
                  <span className="text-slate-400 flex items-center gap-1.5"><Globe className="w-3.5 h-3.5" /> Type</span>
                  <Badge variant="outline" className="border-slate-600 text-slate-300">{assetDetail.type}</Badge>
                </div>
                {assetDetail.last_updated && (
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-slate-400">Last Updated</span>
                    <span className="text-slate-300 text-xs">{new Date(assetDetail.last_updated).toLocaleString()}</span>
                  </div>
                )}
              </div>

              {/* Holding info if user owns this */}
              {portfolio?.holdings?.find(h => h.symbol === assetDetail.symbol) && (() => {
                const h = portfolio.holdings.find(h => h.symbol === assetDetail.symbol);
                const value = h.units * h.current_price_vd;
                const pnl = value - (h.units * h.buy_price_vd);
                return (
                  <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                    <p className="text-emerald-400 text-xs font-medium mb-2">Your Position</p>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div>
                        <p className="text-slate-400 text-[10px]">Value</p>
                        <p className="text-white font-medium text-sm">V${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                      </div>
                      <div>
                        <p className="text-slate-400 text-[10px]">Buy Price</p>
                        <p className="text-white font-medium text-sm">V${h.buy_price_vd?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
                      </div>
                      <div>
                        <p className="text-slate-400 text-[10px]">P&L</p>
                        <p className={`font-medium text-sm ${pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {pnl >= 0 ? '+' : ''}V${pnl.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Financial Fundamentals */}
              {fundamentalsLoading && (
                <div className="flex items-center gap-2 py-3">
                  <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
                  <span className="text-slate-500 text-sm">Loading financials...</span>
                </div>
              )}
              {fundamentals && (
                <div className="space-y-3">
                  <p className="text-slate-300 text-xs font-semibold uppercase tracking-wider border-b border-slate-700 pb-1">Financials</p>
                  
                  {/* Valuation */}
                  <div className="grid grid-cols-3 gap-2">
                    {fundamentals.market_cap_fmt && (
                      <div className="p-2 rounded bg-slate-800/50 text-center">
                        <p className="text-[10px] text-slate-500">Market Cap</p>
                        <p className="text-white text-sm font-medium">{fundamentals.market_cap_fmt}</p>
                      </div>
                    )}
                    {fundamentals.pe_trailing && (
                      <div className="p-2 rounded bg-slate-800/50 text-center">
                        <p className="text-[10px] text-slate-500">P/E Ratio</p>
                        <p className="text-white text-sm font-medium">{fundamentals.pe_trailing}</p>
                      </div>
                    )}
                    {fundamentals.eps && (
                      <div className="p-2 rounded bg-slate-800/50 text-center">
                        <p className="text-[10px] text-slate-500">EPS</p>
                        <p className="text-white text-sm font-medium">${fundamentals.eps}</p>
                      </div>
                    )}
                  </div>

                  {/* Dividends */}
                  {(fundamentals.dividend_yield || fundamentals.dividend_rate) && (
                    <div className="grid grid-cols-2 gap-2">
                      {fundamentals.dividend_yield != null && (
                        <div className="p-2 rounded bg-amber-500/10 border border-amber-500/20 text-center">
                          <p className="text-[10px] text-amber-400/70">Dividend Yield</p>
                          <p className="text-amber-300 text-sm font-medium">{fundamentals.dividend_yield}%</p>
                        </div>
                      )}
                      {fundamentals.dividend_rate && (
                        <div className="p-2 rounded bg-amber-500/10 border border-amber-500/20 text-center">
                          <p className="text-[10px] text-amber-400/70">Annual Dividend</p>
                          <p className="text-amber-300 text-sm font-medium">${fundamentals.dividend_rate}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* P&L / Revenue */}
                  {(fundamentals.revenue_fmt || fundamentals.profit_margin) && (
                    <div className="grid grid-cols-2 gap-2">
                      {fundamentals.revenue_fmt && (
                        <div className="p-2 rounded bg-slate-800/50 text-center">
                          <p className="text-[10px] text-slate-500">Revenue</p>
                          <p className="text-white text-sm font-medium">{fundamentals.revenue_fmt}</p>
                        </div>
                      )}
                      {fundamentals.profit_margin && (
                        <div className="p-2 rounded bg-slate-800/50 text-center">
                          <p className="text-[10px] text-slate-500">Profit Margin</p>
                          <p className={`text-sm font-medium ${fundamentals.profit_margin >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {fundamentals.profit_margin}%
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Balance Sheet */}
                  {(fundamentals.total_cash_fmt || fundamentals.total_debt_fmt) && (
                    <div className="grid grid-cols-2 gap-2">
                      {fundamentals.total_cash_fmt && (
                        <div className="p-2 rounded bg-slate-800/50 text-center">
                          <p className="text-[10px] text-slate-500">Cash</p>
                          <p className="text-emerald-400 text-sm font-medium">{fundamentals.total_cash_fmt}</p>
                        </div>
                      )}
                      {fundamentals.total_debt_fmt && (
                        <div className="p-2 rounded bg-slate-800/50 text-center">
                          <p className="text-[10px] text-slate-500">Total Debt</p>
                          <p className="text-red-400 text-sm font-medium">{fundamentals.total_debt_fmt}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Extra metrics */}
                  <div className="grid grid-cols-3 gap-2">
                    {fundamentals.beta && (
                      <div className="p-2 rounded bg-slate-800/50 text-center">
                        <p className="text-[10px] text-slate-500">Beta</p>
                        <p className="text-white text-sm font-medium">{fundamentals.beta}</p>
                      </div>
                    )}
                    {fundamentals.return_on_equity && (
                      <div className="p-2 rounded bg-slate-800/50 text-center">
                        <p className="text-[10px] text-slate-500">ROE</p>
                        <p className="text-white text-sm font-medium">{fundamentals.return_on_equity}%</p>
                      </div>
                    )}
                    {fundamentals.fifty_two_week_high && (
                      <div className="p-2 rounded bg-slate-800/50 text-center">
                        <p className="text-[10px] text-slate-500">52W Range</p>
                        <p className="text-white text-xs font-medium">${fundamentals.fifty_two_week_low} - ${fundamentals.fifty_two_week_high}</p>
                      </div>
                    )}
                  </div>

                  {/* Industry & Employees */}
                  {(fundamentals.industry || fundamentals.employees) && (
                    <div className="text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
                      {fundamentals.industry && <span>Industry: {fundamentals.industry}</span>}
                      {fundamentals.employees && <span>Employees: {fundamentals.employees.toLocaleString()}</span>}
                    </div>
                  )}

                  {/* Disclaimer */}
                  <div className="p-2 rounded bg-slate-800/30 border border-slate-700/50">
                    <p className="text-[10px] text-slate-500 leading-relaxed">
                      <AlertCircle className="w-3 h-3 inline mr-1 -mt-0.5" />
                      Financial data is provided for game context only and may be delayed. This is not investment advice. Trader Titans uses virtual currency — no real money is at risk. Always do your own research before making real-world financial decisions.
                    </p>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
    </div>
  );
};

// Strategy Option Component
const StrategyOption = ({ id, title, description, icon, selected, onSelect }) => (
  <div 
    className={`p-4 rounded-lg border cursor-pointer transition-all ${
      selected 
        ? 'bg-purple-500/10 border-purple-500/50' 
        : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
    }`}
    onClick={onSelect}
    data-testid={`strategy-${id}`}
  >
    <div className="flex items-start gap-3">
      <div className={`p-2 rounded-lg ${selected ? 'bg-purple-500/20 text-purple-400' : 'bg-slate-700 text-slate-400'}`}>
        {icon}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <h4 className={`font-medium ${selected ? 'text-purple-400' : 'text-white'}`}>{title}</h4>
          {selected && <Check className="h-4 w-4 text-purple-400" />}
        </div>
        <p className="text-sm text-slate-400 mt-1">{description}</p>
      </div>
    </div>
  </div>
);

export default PortfolioPage;
